import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import type { DisplaySession } from '../SessionCard';
import { ProjectTypeIcon } from '../ProjectTypeIcon';
import { relativeDayLabel } from './relativeTime';
import { Touchable } from '../touchable/Touchable';

interface ResumeCardProps {
  session: DisplaySession;
  now: Date;
  onResume: (sessionId: string) => void;
}

export const ResumeCard: React.FC<ResumeCardProps> = ({ session, now, onResume }) => {
  const { t } = useTranslation('session');
  const lastKnit = relativeDayLabel(session.updatedAt, now);

  return (
    <Touchable
      haptic="tap"
      aria-label={session.title}
      onClick={() => onResume(session.id)}
      className="w-full text-left flex items-center gap-3 bg-primary-soft rounded-3xl p-4 active:scale-[0.98] transition-transform focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <ProjectTypeIcon
        metadata={session.metadata}
        color={session.color}
        size={40}
        className="shrink-0"
      />
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-ink truncate">{session.title}</span>
        <span className="block text-xs text-ink-faint">
          {t('home.resume.rowOf', 'Row {{row}} / {{total}}', {
            row: session.currentRow + 1,
            total: session.totalRows,
          })}{' '}
          · {t('home.resume.lastKnit', 'last knit {{when}}', { when: lastKnit })}
        </span>
        <span className="block mt-2 h-1.5 rounded-full bg-base overflow-hidden">
          <span
            data-testid="resume-progress"
            className="block h-full bg-primary rounded-full"
            style={{ width: `${session.progress}%` }}
          />
        </span>
      </span>
      <ChevronRight className="w-6 h-6 text-ink-faint shrink-0" />
    </Touchable>
  );
};
